// tosContent.js
//
// Cuerpo de los Términos de Uso (click-through) Tipo A, para profesionales de la
// salud. Lo muestran el TosAcceptanceGate (texto que se acepta) y la página
// pública /terms-and-conditions (content/tosPorUsuario.js): una sola fuente.
// Fuente original: proyectos_extra/operacional/docs_legales/terminos_uso_clickthrough.md.
//
// La versión llega por parámetro (settings.CURRENT_TOS_VERSION del backend,
// expuesta en /api/user/me/). Cambiar este texto exige subir esa versión en el
// backend: si no, la misma versión apuntaría a dos textos y no habría cómo
// probar cuál aceptó cada médico.
//
// La sección 3 (datos personales) vive en tosDatosPersonales.js y dice lo
// mismo que la Política de Privacidad mínima v2.0 (Planner 347, 05-oct-2026).
// Regla de redacción: nada falso y nada que confiese una brecha
// (feedback_no_publicar_en_contra); sin em dash; con tú.

import { PRIVACIDAD_EMAIL, POLITICA_URL, seccionDatosPersonales } from "./tosDatosPersonales";

import { getTosMarkdownV1 } from "./tosVersionesAnteriores";

// Un solo correo de contacto en los Términos: el mismo que publica la política.
export const TOS_CONTACT_EMAIL = PRIVACIDAD_EMAIL;
export const ARCOP_CONTACT_EMAIL = PRIVACIDAD_EMAIL;

const seccion3 = seccionDatosPersonales({
  rolPacientes:
    "- **Información de tus pacientes:** tú (o la institución de salud para la que trabajas) eres\n" +
    "  el Responsable de su tratamiento, y SmartDx (Salud Wild SpA) actúa como **Encargado**: presta\n" +
    "  el servicio según tus instrucciones.",
  cooperacionPaciente:
    "- Si un paciente quiere ejercer sus derechos sobre información clínica tratada mediante\n" +
    "  SmartDx, debe dirigirse en primer término a ti o a la institución tratante, que es el\n" +
    "  Responsable de esos datos. SmartDx coopera contigo para atenderla dentro de los plazos legales.",
});

const getTosMarkdownBase = (version = "") => `# Términos de Uso de la Plataforma SmartDx
### Modalidad individual para profesionales de la salud (Tipo A)

**Emisor:** Salud Wild SpA, RUT 78.149.483-6 ("SmartDx", "nosotros").
**Versión:** ${version || "vigente"} · **Vigencia:** desde tu aceptación.

Al marcar la casilla **"He leído y acepto los Términos de Uso"** y activar tu cuenta, aceptas
estos Términos. Si contratas en representación de tu empresa (SpA / E.I.R.L.), declaras estar
facultado para obligarla. Estos Términos, junto con la
[Política de Privacidad](${POLITICA_URL}) y, cuando exista, el Contrato SaaS suscrito
entre las partes, regulan el uso de la Plataforma SmartDx.

---

## 1. Qué es SmartDx (y qué no es)

SmartDx es una **herramienta de apoyo a la documentación clínica**, asistida con inteligencia
artificial. Te ayuda a redactar, estructurar, editar y visualizar borradores de documentación
clínica (por defecto, del **Modo Turno**: evoluciones, ingresos, pendientes, indicaciones y
resúmenes por paciente de tu ronda o turno).

**SmartDx NO es un dispositivo médico y no reemplaza tu juicio clínico.** No emite diagnósticos,
indicaciones terapéuticas, alertas, priorizaciones ni recomendaciones clínicas autónomas. Todo
lo que genera es un **borrador preliminar y editable**: tú lo revisas, corriges, validas y, en
su caso, firmas antes de usarlo, comunicarlo o incorporarlo a la ficha clínica. La decisión
clínica final es siempre tuya.

## 2. Tu responsabilidad como profesional

Al usar SmartDx declaras y aceptas que:

- Eres un profesional de la salud habilitado y usas la Plataforma para **fines profesionales y
  asistenciales**, sólo respecto de pacientes en cuya atención intervienes directamente como
  profesional tratante.
- **Toda** evaluación, diagnóstico, indicación, tratamiento, decisión clínica, revisión,
  validación, firma e incorporación a la ficha clínica de cualquier borrador generado con
  SmartDx es de tu **exclusiva responsabilidad** profesional, clínica, ética y legal.
- Cuentas con las autorizaciones, permisos y bases legales necesarias para tratar en la
  Plataforma la información y los datos de tus pacientes (incluidos datos sensibles de salud), y
  con los perfiles de acceso institucionales cuando uses información bajo custodia de una
  clínica, hospital u otra institución.
- La incorporación de los documentos definitivos a la ficha clínica es tuya (o de la institución
  que corresponda), y la custodia de la ficha clínica corresponde a ti o a la institución. SmartDx
  no reemplaza la ficha clínica: la información que tratas en el Modo Turno se conserva por los
  plazos que indica la Política de Privacidad.

${seccion3}

## 4. Licencia de uso

Te otorgamos una licencia **limitada, temporal, no exclusiva, intransferible, no sublicenciable
y revocable** para usar la Plataforma durante la vigencia del servicio y sólo para tus fines
profesionales. La propiedad intelectual de SmartDx (software, modelos, interfaces, marca) es y
seguirá siendo de Salud Wild SpA. Los **outputs** que generes a partir de tu información son
tuyos (o de tu institución).

Te comprometes a **no** compartir tus credenciales (son personales e intransferibles), ni hacer
ingeniería reversa, copia, extracción masiva, scraping, reventa o cualquier uso no autorizado de
la Plataforma, y a avisarnos ante cualquier falla, acceso no autorizado o incidente que
detectes.

## 5. Precio

El precio, la modalidad de pago y las condiciones económicas del servicio se rigen por el
**Contrato SaaS** suscrito entre las partes (o por la orden de contratación aceptada dentro de
la app, cuando corresponda). La falta de pago faculta a SmartDx a suspender el acceso.

## 6. Disponibilidad y responsabilidad

- Hacemos esfuerzos comercialmente razonables por mantener la Plataforma disponible, pero puede
  haber interrupciones (mantenciones, fallas de terceros, fuerza mayor). No garantizamos
  operación ininterrumpida ni libre de errores.
- SmartDx **no responde** por decisiones clínicas ni por errores derivados de la información que
  tú o terceros ingresen. No respondemos por daños indirectos, lucro cesante ni perjuicios
  consecuenciales.
- Nuestra responsabilidad total acumulada no excederá las sumas que nos hayas pagado en los seis
  (6) meses anteriores al hecho que origine el reclamo.

## 7. Vigencia y término

Estos Términos rigen mientras uses la Plataforma. Cualquiera de las partes puede terminar el
servicio conforme al Contrato SaaS aplicable (por defecto: período piloto acordado, renovable
por períodos mensuales; no renovación con aviso de 15 días corridos; término inmediato por
incumplimiento grave no subsanado). El término no libera de pagos ya devengados.

## 8. Ley aplicable y jurisdicción

Estos Términos se rigen por la ley chilena. Cualquier controversia se somete a los Tribunales
Ordinarios de Justicia de Santiago de Chile.

## 9. Aceptación

Al marcar **"He leído y acepto los Términos de Uso"** manifiestas tu consentimiento mediante
firma electrónica simple (Ley N° 19.799). Dejaremos registro de tu identidad de usuario, fecha y
hora de aceptación y versión aceptada de estos Términos.

Si cambiamos estos Términos de forma sustancial, te avisaremos por correo electrónico o dentro de
la plataforma y te pediremos aceptar la nueva versión para seguir usándola.

Contacto: ${TOS_CONTACT_EMAIL} · Salud Wild SpA · Antonio Bellet 193, of. 1210, Providencia,
Santiago.

---

> **Recuerda:** SmartDx es un asistente de redacción clínica. Los borradores son preliminares y
> editables, y **no reemplazan tu juicio clínico** como profesional tratante.
`;

// ── #1683 · cláusula de comunicaciones ───────────────────────────────────────
// La sección "3 bis" (design.md D10 de .claude/specs/1683-derechos-usuario/, a revisar con
// Dentons) va SOLO en las versiones de este set, que son las del bump único (#1675, #1681,
// #347): texto de este archivo + 3 bis. Una versión ya aceptada no cambia de texto (R47): las
// de VERSIONES_V1 devuelven el texto CONGELADO de tosVersionesAnteriores.js, que es lo que
// firmó quien aceptó una v1.x. Cualquier OTRA versión (un bump futuro que olvidó este set, un
// error de tipeo en el .env) muestra el texto nuevo sin la 3 bis, nunca el retirado (revisor
// del bump, 20-oct-2026, M7: con "todo lo que no es v2.0 → congelado", v2.1 firmaba el viejo).
// Cubre R46, R47 — .claude/specs/1683-derechos-usuario/
// 26-oct-2026 (#1770b): "v2.1" también lleva la 3 bis; si no estuviera acá, la v2.1 la perdería en silencio.
export const VERSIONES_CON_COMUNICACIONES = new Set(["v2.0", "v2.1", "v2.0-interno"]);
// Todas las versiones que existieron antes del bump único (back: settings.CURRENT_TOS_VERSION
// v1.0; TOS_VERSION_BY_PROFESSION v1.0-interno a v1.4-interno) y "" (sin versión).
export const VERSIONES_V1 = new Set([
  "", "v1.0", "v1.1", "v1.2", "v1.3",
  "v1.0-interno", "v1.1-interno", "v1.2-interno", "v1.3-interno", "v1.4-interno",
]);

// La versión que exige hoy el backend (settings.CURRENT_TOS_VERSION y
// TOS_VERSION_BY_PROFESSION) cuando todavía no hay /api/user/me/: el registro
// (AuthenticationForm) y la página pública sin sesión. Tiene que coincidir con el backend
// (revisor del bump, 20-oct-2026, A1: el registro mostraba "Versión: vigente" sin la 3 bis
// mientras el backend guardaba v2.0). Si el front sube antes que el back, quien se registre en
// esa ventana queda con la versión vieja guardada y el gate le pide la v2.0 después del bump.
// #1770b: Tipo A pasa a "v2.1" (con la 3 ter). Par del back: settings.CURRENT_TOS_VERSION, y
// user_api/tos.py:VERSIONES_CON_CLAUSULA_MEJORA = este set. Viajan en el mismo paquete, el front PRIMERO:
// el POST de aceptar manda esta versión (TosAcceptanceGate.js) y el back responde 409 si no coincide.
export const TOS_VERSION_VIGENTE = "v2.1";
export const TOS_VERSION_VIGENTE_INTERNO = "v2.0-interno";

export const SECCION_COMUNICACIONES = `## 3 bis. Comunicaciones y eliminación de tu cuenta

- Te escribimos por correo para ayudarte a usar SmartDx: primeros pasos, recordatorios de lo que
  dejaste a medias y novedades. Puedes dejar de recibir esos correos cuando quieras desde tu
  Cuenta o con el enlace que trae cada uno.
- Al aceptar estos Términos nos autorizas a escribirte por WhatsApp, al celular que nos des,
  desde el número de SmartDx y solo para ayudarte a usar SmartDx: primeros pasos, recordatorios
  y novedades de la app. Aceptar esto no es una condición para usar SmartDx: puedes quitar la
  autorización cuando quieras desde tu Cuenta, con el enlace de cualquier correo nuestro o
  respondiendo BAJA, y desde ese momento no te escribimos más por WhatsApp. Si eres tú quien nos
  escribe, te respondemos por ese mismo chat.
- Los avisos de seguridad de tu cuenta, la recuperación de tu clave y los avisos de cobro,
  vencimiento o suspensión de tu suscripción se envían siempre, porque son parte del servicio.
- Puedes eliminar tu cuenta desde tu Cuenta. Borramos tu información y la de tus pacientes,
  salvo lo que la ley nos obliga a conservar: las boletas y facturas de lo que pagaste, el
  registro de tu aceptación de estos Términos y el registro de las fichas de pacientes que
  abriste, con tu correo, hasta 2 años. La conversación de WhatsApp queda en nuestro registro de
  atención; pídenos borrarla por WhatsApp. Las copias de respaldo se renuevan solas, así que tus
  datos pueden tardar un tiempo en desaparecer de ellas.
`;

// Inserta la sección justo antes de la "4." (después de "3. Datos personales y privacidad"),
// y solo si `version` está en el set. Si el ancla no está (alguien renumeró los Términos) NO la
// pone en silencio en cualquier parte: lanza, y el test lo ve.
export const conComunicaciones = (texto, version) => {
  if (!VERSIONES_CON_COMUNICACIONES.has(version)) return texto;
  const ancla = "\n## 4. Licencia de uso";
  if (!texto.includes(ancla)) {
    throw new Error("tosContent: no encuentro la sección 4 para insertar la cláusula de comunicaciones");
  }
  return texto.replace(ancla, `\n${SECCION_COMUNICACIONES}${ancla}`);
};

// ── #1770b · cláusula de mejora de la lectura de listas ──────────────────────
// Cubre R14, R15 — .claude/specs/1770b-clausula-mejora-terminos/
// La sección "3 ter" (redacción B, elegida por Sebastián el 26-oct-2026) va SOLO en las versiones
// Tipo A de este set. Conjunto explícito y no "mayor o igual que v2.1": "v2.10" < "v2.9" como texto,
// y una versión que nadie agregó acá NO muestra la cláusula, igual que el back no captura para ella
// (user_api/tos.py:VERSIONES_CON_CLAUSULA_MEJORA, mismo valor). La v2.0 ya aceptada y todo el Tipo C
// conservan su texto carácter por carácter (R15, regla R47 de #1683).
// Cada frase tiene su sostén en el código de #1770 (design.md de 1770b, tabla "Por qué cada frase es
// verdadera"): si cambian los plazos de purga o el acceso, esta sección se revisa en el mismo cambio.
export const VERSIONES_CON_CLAUSULA_MEJORA = new Set(["v2.1"]);

export const SECCION_CLAUSULA_MEJORA = `## 3 ter. Mejora de la lectura de listas de pacientes (Sincronizar camas)

- **Qué nos autorizas.** Al aceptar estos Términos nos autorizas a guardar una copia del texto de las
  listas de pacientes que pegas en Sincronizar camas, con los datos de pacientes que traiga (por
  ejemplo nombres, RUT, camas y diagnósticos). Si esa información es de una institución de salud,
  declaras contar con su autorización para darnos esta.
- **Para qué.** Solo para revisar y mejorar cómo SmartDx lee esas listas: encontrar los formatos que
  lee mal y probar los cambios antes de aplicarlos en el servicio de todos los profesionales. No lo
  usamos para otro fin, no lo comunicamos a terceros (se guarda en la infraestructura en la nube
  indicada en la sección 3), y el texto guardado no se envía a ningún modelo de inteligencia
  artificial ni se usa para entrenarlos.
- **Cuándo y por cuánto tiempo.** Lo guardamos en períodos acotados, mientras trabajamos en esa
  mejora. Cada texto se guarda cifrado y se elimina de la plataforma a más tardar 15 días después de
  que lo pegaste; los que SmartDx leyó sin problemas, a más tardar 4 días. Las copias de respaldo se
  eliminan según sus ciclos.
- **Quién lo ve.** Solo una persona autorizada de Salud Wild SpA, con un acceso propio que deja
  registro.
- **Contratos.** Si tienes un contrato firmado con Salud Wild SpA que limita el uso de los datos a la
  prestación del servicio, rige ese contrato y no guardamos estos textos.
- **Cómo no participar.** Puedes pedirlo cuando quieras escribiendo a ${TOS_CONTACT_EMAIL}, sin que
  cambie tu servicio. Desde que registremos tu solicitud no guardamos más textos tuyos y borramos los
  que ya estaban guardados.
`;

// Inserta la 3 ter justo antes de la "4." (queda 3 bis, 3 ter, 4: se aplica DESPUÉS de conComunicaciones).
// Sin el ancla lanza, como conComunicaciones: nunca la pone en cualquier parte.
export const conClausulaMejora = (texto, version) => {
  if (!VERSIONES_CON_CLAUSULA_MEJORA.has(version)) return texto;
  const ancla = "\n## 4. Licencia de uso";
  if (!texto.includes(ancla)) {
    throw new Error("tosContent: no encuentro la sección 4 para insertar la cláusula de mejora");
  }
  return texto.replace(ancla, `\n${SECCION_CLAUSULA_MEJORA}${ancla}`);
};

/** Términos Tipo A: una v1.x, su texto congelado (R47); cualquier otra, el texto nuevo (+ 3 bis y 3 ter si están en sus sets). */
export const getTosMarkdown = (version = "") =>
  VERSIONES_V1.has(version)
    ? getTosMarkdownV1(version)
    : conClausulaMejora(conComunicaciones(getTosMarkdownBase(version), version), version);

/** Solo para el test de R46: el texto v2.0 SIN la 3 bis. */
export const getTosMarkdownSinComunicaciones = getTosMarkdownBase;

export default getTosMarkdown;
